import React from 'react'
import {
  Artifact,
  ArtifactType as ArtifactTypeEnum,
  ControlsArtifact,
  CostEstimateArtifact,
  FrameworkDocArtifact,
  FrameworkSectionArtifact,
  OeControlTestCitationType,
  PoliciesArtifact,
  PlaybookParamInputArtifact,
  ReportType,
  SelectionArtifact,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import {
  EnforcementMode,
  HighlightCondition,
  ThresholdOperator,
} from '@trustero/trustero-api-web/lib/auditbotapi/auditbotapi_pb'
import { SOURCE_RESULT } from '@trustero/trustero-api-web/lib/source/source_pb'
import { GridRowClassNameParams } from '@mui/x-data-grid'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { LearnMoreLink } from 'src/components/Reusable/Text/Link'
import { HelpPagesInternal } from 'src/pages/Help/Help.constants'
import { cleanTestProcedureDescription } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import {
  TestTableDescription,
  TestTableDescriptionSection,
  TestTableDescriptionSectionHeader,
} from 'src/components/ModalForms/Evidence/evidence.styles'
import { getTestTableResult } from 'src/components/ModalForms/Evidence/ViewTestTableSourceModal'
import { ColumnHeaderTooltip } from 'src/Utils/Evidence/evidence.types'
import { ControlCheckSourceTitle } from './AuditScan/AuditScanArtifact.components'
import { AuditScanArtifactJoinInfo } from './AuditScan/AuditScanArtifact.constants'

export const getDisplayName = (artifact: Artifact): string => {
  const type = artifact.getType()
  switch (type) {
    case ArtifactTypeEnum.ARTIFACT_UNSPECIFIED:
      return 'Unknown Artifact'
    case ArtifactTypeEnum.ARTIFACT_MIME:
      return 'File'
    case ArtifactTypeEnum.ARTIFACT_POLICIES:
      return 'Policies'
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_DOC:
      return 'Framework'
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_QUESTIONNAIRE:
      return 'Policy Design Assessment'
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_SECTION:
      return 'Framework Sections'
    case ArtifactTypeEnum.ARTIFACT_COST_ESTIMATE:
      return 'Cost Estimate'
    case ArtifactTypeEnum.ARTIFACT_REPORT:
      return artifact.getReportArtifact()?.getType() === ReportType.REPORT_PDA
        ? 'Policy Design Assessment Report'
        : 'Report'
    case ArtifactTypeEnum.ARTIFACT_CONTROLS:
      return 'Controls'
    case ArtifactTypeEnum.ARTIFACT_TEST_TABLE:
      return 'Test Table'
    case ArtifactTypeEnum.ARTIFACT_CROSS_REFERENCED_DATA:
      return 'Cross Referenced Data'
    case ArtifactTypeEnum.ARTIFACT_SAMPLED_EVIDENCE:
      return 'Sampled Evidence'
    case ArtifactTypeEnum.ARTIFACT_FILTERED_EVIDENCE:
      return 'Filtered Evidence'
    case ArtifactTypeEnum.ARTIFACT_SELECTION:
      return artifact.getSelectionArtifact()?.getTitle() || 'Selection'
    case ArtifactTypeEnum.ARTIFACT_OE_CONTROL_RESULTS:
      return 'Control Test Results'
    case ArtifactTypeEnum.ARTIFACT_CONTROL_POLICY_MATERIAL_DIFFERENCE_CHECKS:
      return 'Control-Policy Material Difference'
    case ArtifactTypeEnum.ARTIFACT_TEST_PROCEDURE_REFINEMENT: {
      const v = artifact.getTestProcedureRefinementArtifact()?.getVersion()
      return v
        ? `Proposed Test Procedures - Version ${v}`
        : 'Proposed Test Procedures'
    }
    case ArtifactTypeEnum.ARTIFACT_PLAYBOOK_PARAM_INPUT: {
      const paramInput = artifact.getPlaybookParamInputArtifact()
      const paramName = paramInput?.getParamName() || 'Input'
      const paramType = paramInput?.getParameterType()
      const typePrefix = getParameterTypeDisplayPrefix(paramType)
      return typePrefix ? `${typePrefix}: ${paramName}` : paramName
    }
    default:
      return 'Unknown Artifact'
  }
}

export const getQuantifiedData = (artifact: Artifact): string | undefined => {
  const artifactType = artifact.getType()

  switch (artifactType) {
    case ArtifactTypeEnum.ARTIFACT_COST_ESTIMATE:
      return getCostEstimateData(artifact.getCostEstimateArtifact())
    case ArtifactTypeEnum.ARTIFACT_POLICIES:
      return getPoliciesData(artifact.getPoliciesArtifact())
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_DOC:
      return getFramework(artifact.getFrameworkDocArtifact())
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_SECTION:
      return getFrameworkSections(artifact.getFrameworkSectionArtifact())
    case ArtifactTypeEnum.ARTIFACT_CONTROLS:
      return getControlsData(artifact.getControlsArtifact())
    case ArtifactTypeEnum.ARTIFACT_SELECTION:
      return getSelectionData(artifact.getSelectionArtifact())
    case ArtifactTypeEnum.ARTIFACT_PLAYBOOK_PARAM_INPUT:
      return getPlaybookParamInputData(artifact.getPlaybookParamInputArtifact())
    case ArtifactTypeEnum.ARTIFACT_CONTROL_POLICY_MATERIAL_DIFFERENCE_CHECKS: {
      const cpmd = artifact.getControlPolicyMaterialDifferenceChecksArtifact()
      if (!cpmd) return ''
      const checks = cpmd.getChecksList()
      const satisfied = checks.filter(
        (c) => c.getSatisfied() && !c.getError(),
      ).length
      return `${checks.length} checked, ${satisfied} covered`
    }
    default:
      return ''
  }
}

export const canSaveAsEvidence = (artifactType: ArtifactTypeEnum): boolean =>
  artifactType === ArtifactTypeEnum.ARTIFACT_TEST_TABLE ||
  artifactType === ArtifactTypeEnum.ARTIFACT_CROSS_REFERENCED_DATA ||
  artifactType === ArtifactTypeEnum.ARTIFACT_SAMPLED_EVIDENCE ||
  artifactType === ArtifactTypeEnum.ARTIFACT_FILTERED_EVIDENCE

export const getActionName = (
  artifactType: ArtifactTypeEnum,
  isDisabled: boolean,
): string => {
  if (artifactType === ArtifactTypeEnum.ARTIFACT_COST_ESTIMATE) {
    return ''
  }
  if (isDisabled) {
    return 'View'
  }
  switch (artifactType) {
    case ArtifactTypeEnum.ARTIFACT_POLICIES:
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_DOC:
    case ArtifactTypeEnum.ARTIFACT_FRAMEWORK_SECTION:
    case ArtifactTypeEnum.ARTIFACT_SELECTION:
    case ArtifactTypeEnum.ARTIFACT_PLAYBOOK_PARAM_INPUT:
    case ArtifactTypeEnum.ARTIFACT_TEST_PROCEDURE_REFINEMENT:
      return 'Edit'
    default:
      return 'View'
  }
}

const getCostEstimateData = (
  costEstimate?: CostEstimateArtifact,
): string | undefined => {
  if (!costEstimate) {
    return undefined
  }

  return `${costEstimate.getCost()} ${costEstimate.getUnit()}`
}

const getPoliciesData = (policies?: PoliciesArtifact): string | undefined => {
  if (!policies) {
    return undefined
  }

  return policies
    .getPoliciesList()
    .flatMap((policy) =>
      policy.getPolicyDocsList().filter((doc) => doc.getSelected()),
    )
    .length.toString()
}

const getControlsData = (controls?: ControlsArtifact): string | undefined => {
  if (!controls) {
    return undefined
  }

  return controls
    .getControlsList()
    .filter((control) => control.getSelected())
    .length.toString()
}

const getSelectionData = (
  selection?: SelectionArtifact,
): string | undefined => {
  if (!selection) {
    return undefined
  }

  return selection
    .getSelectOptionsList()
    .filter((option) => option.getSelected())
    .length.toString()
}

const getFramework = (framework?: FrameworkDocArtifact): string => {
  if (!framework) {
    return 'No framework selected'
  }

  return (
    framework
      .getFrameworkDocsList()
      .find((doc) => doc.getSelected())
      ?.getFrameworkName() || 'No framework selected'
  )
}

const getFrameworkSections = (
  frameworkSections?: FrameworkSectionArtifact,
): string => {
  if (!frameworkSections) {
    return 'No framework sections selected'
  }

  return frameworkSections
    .getSectionsList()
    .filter((section) => section.getSelected())
    .length.toString()
}

const getPlaybookParamInputData = (
  paramInput?: PlaybookParamInputArtifact,
): string | undefined => {
  if (!paramInput) {
    return undefined
  }

  const paramType = paramInput.getParameterType()
  if (paramType === 'text' || paramType === 'boolean') {
    return undefined
  }

  const selectedCount = paramInput
    .getOptionsList()
    .filter((option) => option.getSelected()).length

  return selectedCount.toString()
}

// Helper to get display prefix for parameter types
const getParameterTypeDisplayPrefix = (
  paramType: string | undefined,
): string | null => {
  switch (paramType) {
    case 'control':
      return 'Control'
    case 'policy':
      return 'Policy'
    case 'vendor':
      return 'Vendor'
    case 'document':
      return 'Document'
    case 'text':
      return 'Text'
    case 'boolean':
      return 'Boolean'
    default:
      return null
  }
}

export const getArtifactCsv = (artifact: Artifact): string => {
  const type = artifact.getType()
  if (type === ArtifactTypeEnum.ARTIFACT_TEST_TABLE) {
    return artifact.getTestTableArtifact()?.getTestTable()?.getTable() || ''
  }
  if (type === ArtifactTypeEnum.ARTIFACT_CROSS_REFERENCED_DATA) {
    return artifact.getCrossReferencedDataArtifact()?.getTable() || ''
  }
  if (type === ArtifactTypeEnum.ARTIFACT_SAMPLED_EVIDENCE) {
    return artifact.getSampledEvidenceArtifact()?.getTable() || ''
  }
  if (type === ArtifactTypeEnum.ARTIFACT_FILTERED_EVIDENCE) {
    return artifact.getFilteredEvidenceArtifact()?.getTable() || ''
  }
  return ''
}

export const getArtifactDescription = (artifact: Artifact): string => {
  const type = artifact.getType()
  if (type === ArtifactTypeEnum.ARTIFACT_CROSS_REFERENCED_DATA) {
    const crd = artifact.getCrossReferencedDataArtifact()
    const joinDescription = crd?.getJoinDescription() || ''
    const joinCriteria = crd?.getJoinCriteria() || ''
    return [joinDescription, joinCriteria].filter(Boolean).join(' ')
  }
  if (type === ArtifactTypeEnum.ARTIFACT_TEST_TABLE) {
    return artifact.getTestTableArtifact()?.getTestTable()?.getTest() || ''
  }
  if (type === ArtifactTypeEnum.ARTIFACT_FILTERED_EVIDENCE) {
    return artifact.getFilteredEvidenceArtifact()?.getFilterDescription() || ''
  }
  return ''
}

export const getArtifactPreviewDescription = (
  artifact: Artifact,
): JSX.Element | null => {
  const type = artifact.getType()

  if (type === ArtifactTypeEnum.ARTIFACT_CROSS_REFERENCED_DATA) {
    const crd = artifact.getCrossReferencedDataArtifact()
    if (!crd) return null
    return (
      <TestTableDescription>
        <TestTableDescriptionSection>
          <FlexRow justify={FlexAlign.FLEX_START} isFullWidth>
            {crd.getJoinDescription() || ''}
            {crd.getJoinCriteria() || ''}
          </FlexRow>
          <TestTableDescriptionSectionHeader>
            Sources
          </TestTableDescriptionSectionHeader>
          <ControlCheckSourceTitle
            sourceId={crd.getEvidenceInstanceId1()}
            sourceType={OeControlTestCitationType.EVIDENCE}
            joinInfo={AuditScanArtifactJoinInfo.LEFT}
          />
          <ControlCheckSourceTitle
            sourceId={crd.getEvidenceInstanceId2()}
            sourceType={OeControlTestCitationType.EVIDENCE}
            joinInfo={AuditScanArtifactJoinInfo.RIGHT}
          />
        </TestTableDescriptionSection>
      </TestTableDescription>
    )
  }

  if (type === ArtifactTypeEnum.ARTIFACT_TEST_TABLE) {
    const testTable = artifact.getTestTableArtifact()?.getTestTable()
    if (!testTable) return null
    const passed = testTable.getPassed()
    const count = testTable.getCount()
    const threshold = testTable.getThresholds()?.getThreshold() || 0
    const mode =
      testTable.getThresholds()?.getMode() || EnforcementMode.ABSOLUTE
    const operator =
      testTable.getThresholds()?.getOperator() ||
      ThresholdOperator.GREATER_THAN_OR_EQUAL
    const result = passed ? SOURCE_RESULT.PASS : SOURCE_RESULT.FAIL
    const sourceId = testTable.getSourceId()
    const sourceType = testTable.getSourceType()
    return (
      <TestTableDescription>
        <FlexRow justify={FlexAlign.FLEX_START} isFullWidth>
          This table shows how evidence from a table performed on a specific
          test procedure. &nbsp;
          <LearnMoreLink href={HelpPagesInternal['TestTable'].link} />
        </FlexRow>
        <TestTableDescriptionSection>
          {getTestTableResult(passed, count, threshold, mode, operator)}
        </TestTableDescriptionSection>
        <TestTableDescriptionSection>
          <TestTableDescriptionSectionHeader>
            Procedure
          </TestTableDescriptionSectionHeader>
          <Markup
            markdown={cleanTestProcedureDescription(testTable.getTest())}
          />
        </TestTableDescriptionSection>
        <TestTableDescriptionSection>
          <TestTableDescriptionSectionHeader>
            Source
          </TestTableDescriptionSectionHeader>
          <ControlCheckSourceTitle
            sourceId={sourceId}
            sourceType={sourceType}
            result={result}
          />
        </TestTableDescriptionSection>
      </TestTableDescription>
    )
  }

  if (type === ArtifactTypeEnum.ARTIFACT_SAMPLED_EVIDENCE) {
    const sampledEvidence = artifact.getSampledEvidenceArtifact()
    if (!sampledEvidence) return null
    return (
      <TestTableDescription>
        <FlexRow justify={FlexAlign.FLEX_START} isFullWidth>
          {sampledEvidence.getDescription() || ''}
        </FlexRow>
      </TestTableDescription>
    )
  }

  if (type === ArtifactTypeEnum.ARTIFACT_FILTERED_EVIDENCE) {
    const filteredEvidence = artifact.getFilteredEvidenceArtifact()
    if (!filteredEvidence) return null
    return (
      <TestTableDescription>
        <FlexRow justify={FlexAlign.FLEX_START} isFullWidth>
          {filteredEvidence.getFilterDescription() || ''}
        </FlexRow>
        <TestTableDescriptionSection>
          <TestTableDescriptionSectionHeader>
            Filter
          </TestTableDescriptionSectionHeader>
          Column: {filteredEvidence.getFilteredColumn() || ''} | Regex:{' '}
          <code>{filteredEvidence.getRegexPattern() || ''}</code>
        </TestTableDescriptionSection>
        <TestTableDescriptionSection>
          <TestTableDescriptionSectionHeader>
            Source
          </TestTableDescriptionSectionHeader>
          <ControlCheckSourceTitle
            sourceId={filteredEvidence.getEvidenceInstanceId()}
            sourceType={OeControlTestCitationType.EVIDENCE}
          />
        </TestTableDescriptionSection>
      </TestTableDescription>
    )
  }
  return null
}

export const getTestTableHeaderTooltip = (
  artifact: Artifact,
): ColumnHeaderTooltip | undefined => {
  if (artifact.getType() !== ArtifactTypeEnum.ARTIFACT_TEST_TABLE)
    return undefined
  const explanation = artifact
    .getTestTableArtifact()
    ?.getTestTable()
    ?.getExplanation()
  if (!explanation) return undefined
  return { header: 'Result', tooltip: explanation }
}

export const getHighlightRowClassName = (
  artifact: Artifact,
): ((params: GridRowClassNameParams) => string) | undefined => {
  if (artifact.getType() !== ArtifactTypeEnum.ARTIFACT_TEST_TABLE)
    return undefined
  const highlightCondition = artifact
    .getTestTableArtifact()
    ?.getTestTable()
    ?.getThresholds()
    ?.getHighlight()
  if (highlightCondition === undefined) return undefined
  return (params: GridRowClassNameParams) => {
    const failed =
      (highlightCondition === HighlightCondition.WHEN_FALSE &&
        params.row['Result'] === 'False') ||
      (highlightCondition === HighlightCondition.WHEN_TRUE &&
        params.row['Result'] === 'True')
    return failed ? 'failed' : ''
  }
}
